/**
 * DocumentVerificationStep.jsx
 * KI-gestützte Dokument-Verifikation im Personalbogen
 *
 * DSGVO: Bilder werden NIEMALS gespeichert.
 * GPT-4o prüft ob eingetippte Daten mit Ausweis/IBAN übereinstimmen.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Camera, CheckCircle2, AlertTriangle, X,
  Loader2, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Hilfsfunktion: Datei → base64 ────────────────────────────────────────────
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Foto-Upload-Feld ──────────────────────────────────────────────────────────
function PhotoUpload({ label, value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {value ? (
        <div className="relative">
          <img src={value} alt={label} className="rounded-xl border max-h-40 object-contain w-full bg-muted" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-gray-800 border shadow hover:bg-red-50 text-red-500 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Hochgeladen
          </div>
        </div>
      ) : (
        <label
          className="flex flex-col items-center justify-center gap-2 h-24 rounded-xl border-2 border-dashed border-border cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all"
          data-testid={testId}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const b64 = await fileToBase64(file);
              onChange(b64);
            }}
          />
          <Camera className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Foto aufnehmen oder auswählen</span>
        </label>
      )}
    </div>
  );
}

// ─── Feld-Vergleichs-Zeile ─────────────────────────────────────────────────────
function FieldCompareRow({ feldName, label, result, override, onOverrideChange }) {
  if (!result) return null;
  const { ausweisWert, übereinstimmung, konfidenz, eingetippt } = result;

  const isNull = ausweisWert === null || ausweisWert === undefined;
  const match = isNull ? null : übereinstimmung;

  if (isNull) return null; // Feld nicht lesbar → nicht zeigen

  return (
    <div className={`p-3 rounded-xl border ${
      override ? 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700' :
      match === true ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700' :
      match === false ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-700' :
      'bg-gray-50 dark:bg-gray-800/40 border-gray-200'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {override ? (
              <span className="text-xs text-gray-500 font-medium">{label}</span>
            ) : match === true ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
            ) : match === false ? (
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
            ) : null}
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{label}</span>
            {konfidenz === 'niedrig' && !override && (
              <span className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">
                Niedrige Sicherheit
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 space-y-0.5">
            <div>Eingetippt: <span className="font-mono text-gray-700 dark:text-gray-300">{eingetippt || '—'}</span></div>
            {match === false && !override && (
              <div className="text-red-600 dark:text-red-400 font-medium">
                Auf Ausweis: <span className="font-mono">{ausweisWert}</span>
              </div>
            )}
          </div>
        </div>
        {match === false && (
          <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={override || false}
              onChange={e => onOverrideChange(feldName, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-xs text-gray-500 whitespace-nowrap">Falsch erkannt</span>
          </label>
        )}
      </div>
    </div>
  );
}

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────
export function DocumentVerificationStep({
  token, form,
  frontImage, setFrontImage,
  backImage, setBackImage,
  ibanCardImage, setIbanCardImage,
  verifyStep, setVerifyStep,
  verifyResults, setVerifyResults,
  verifyError, setVerifyError,
  fieldOverrides, setFieldOverrides,
  ibanVerifyResult, setIbanVerifyResult,
  dsgvoConsent, setDsgvoConsent,
  setForm,
  verifyDokumentTyp, setVerifyDokumentTyp,
  verifyAufenthaltstitel, setVerifyAufenthaltstitel,
  sessionHeaders = {},
}) {
  const [showIban, setShowIban] = useState(false);

  const handleVerifyId = async () => {
    if (!frontImage) { toast.error('Bitte Vorderseite des Ausweises hochladen'); return; }
    if (!dsgvoConsent) { toast.error('Bitte zuerst der einmaligen Bildverarbeitung zustimmen'); return; }

    setVerifyStep('verifying');
    setVerifyError(null);
    try {
      const res = await axios.post(`${API}/api/personalbogen/public/${token}/verify-document`, {
        front_image: frontImage,
        back_image: backImage || '',
        typed_data: {
          vorname: form.vorname,
          familienname: form.familienname,
          geburtsdatum: form.geburtsdatum,
          geburtsort: form.geburtsort,
          staatsangehoerigkeit: form.staatsangehoerigkeit,
          strasse: form.strasse,
          hausnummer: form.hausnummer,
          plz: form.plz,
          ort: form.ort,
        },
      }, { headers: sessionHeaders });
      if (res.data.error) {
        setVerifyError(res.data.error);
        setVerifyStep('error');
      } else {
        setVerifyResults(res.data.felder || {});
        setVerifyDokumentTyp && setVerifyDokumentTyp(res.data.dokumentTyp || null);
        setVerifyAufenthaltstitel && setVerifyAufenthaltstitel(res.data.aufenthaltstitel || null);
        setVerifyStep('done');
        // Wenn Aufenthaltstitel erkannt: automatisch in Form übernehmen
        if (res.data.aufenthaltstitel && res.data.aufenthaltstitel.gueltigBis) {
          // Konvertiere TT.MM.JJJJ → YYYY-MM-DD für date input
          const parts = res.data.aufenthaltstitel.gueltigBis.split('.');
          if (parts.length === 3) {
            const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            setForm(f => ({ ...f, aufenthalt_bis: isoDate, aufenthalt_befristet: true }));
          }
        }
        if (res.data.aufenthaltstitel && res.data.aufenthaltstitel.istUnbefristet === true) {
          setForm(f => ({ ...f, aufenthalt_befristet: false, aufenthalt_bis: '' }));
        }
      }
    } catch (e) {
      setVerifyError(e.response?.data?.detail || 'Verifikation fehlgeschlagen');
      setVerifyStep('error');
    }
    // Bilder aus State löschen nach Verarbeitung (DSGVO)
    setFrontImage(null);
    setBackImage(null);
  };

  const handleVerifyIban = async () => {
    if (!ibanCardImage) { toast.error('Bitte Bankkarte fotografieren'); return; }
    try {
      const res = await axios.post(`${API}/api/personalbogen/public/${token}/verify-iban`, {
        card_image: ibanCardImage,
        typed_iban: form.iban?.replace(/\s/g, '') || '',
      }, { headers: sessionHeaders });
      setIbanVerifyResult(res.data);
      // Bild löschen (DSGVO)
      setIbanCardImage(null);
    } catch (e) {
      toast.error('IBAN-Verifikation fehlgeschlagen');
    }
  };

  const handleApplySuggestion = (feldName, ausweisWert) => {
    if (!ausweisWert) return;
    setForm(f => ({ ...f, [feldName]: ausweisWert }));
    toast.success(`${feldName} auf "${ausweisWert}" aktualisiert`);
  };

  const allOk = verifyResults && Object.values(verifyResults).every(
    f => f.übereinstimmung === true || fieldOverrides[Object.keys(verifyResults).find(k => verifyResults[k] === f)]
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800/40 flex items-center justify-center flex-shrink-0">
          <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="text-sm font-bold text-blue-800 dark:text-blue-200">KI-Dokument-Prüfung (optional)</p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
            Fotografiere deinen Ausweis — KI prüft ob deine Angaben korrekt sind. Bilder werden <strong>nicht gespeichert</strong>.
          </p>
        </div>
      </div>

      {/* DSGVO Einwilligung */}
      {verifyStep === 'idle' && (
        <label className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={dsgvoConsent}
            onChange={e => setDsgvoConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300"
            data-testid="verify-dsgvo-consent"
          />
          <div>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Ich stimme der einmaligen KI-Bildverarbeitung zu
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Dein Ausweis-Foto wird ausschließlich zur einmaligen Prüfung an GPT-4o übertragen und danach sofort gelöscht.
              Es wird <strong>nirgendwo gespeichert</strong>. Nur das Prüfergebnis (✓/⚠) bleibt.
            </p>
          </div>
        </label>
      )}

      {/* Ausweis Upload */}
      {verifyStep === 'idle' && (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Personalausweis / Reisepass / Aufenthaltstitel
          </p>
          <div className="grid grid-cols-2 gap-3">
            <PhotoUpload
              label="Vorderseite *"
              value={frontImage}
              onChange={setFrontImage}
              testId="ausweis-front-upload"
            />
            <PhotoUpload
              label="Rückseite (optional)"
              value={backImage}
              onChange={setBackImage}
              testId="ausweis-back-upload"
            />
          </div>
          <Button
            type="button"
            onClick={handleVerifyId}
            disabled={!frontImage || !dsgvoConsent}
            className="w-full h-12"
            data-testid="verify-id-btn"
          >
            <Shield className="h-4 w-4 mr-2" />
            Angaben mit Ausweis prüfen
          </Button>
        </div>
      )}

      {/* Loading */}
      {verifyStep === 'verifying' && (
        <div className="flex items-center justify-center py-10 gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">KI prüft deinen Ausweis…</span>
        </div>
      )}

      {/* Fehler */}
      {verifyStep === 'error' && (
        <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl">
          <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">Prüfung fehlgeschlagen</p>
          <p className="text-xs text-red-600 dark:text-red-400">{verifyError}</p>
          <button
            type="button"
            onClick={() => { setVerifyStep('idle'); setVerifyError(null); }}
            className="mt-2 text-xs text-red-600 underline"
          >
            Nochmal versuchen
          </button>
        </div>
      )}

      {/* Ergebnisse */}
      {verifyStep === 'done' && verifyResults && (
        <div className="space-y-3">
          {/* Dokument-Typ Badge */}
          {verifyDokumentTyp && (
            <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg">
              <Shield className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Erkanntes Dokument: <strong className="text-gray-800 dark:text-gray-200">{
                  { personalausweis: 'Personalausweis', reisepass: 'Reisepass', aufenthaltstitel: 'Aufenthaltstitel', niederlassungserlaubnis: 'Niederlassungserlaubnis', blaue_karte_eu: 'Blaue Karte EU', fiktionsbescheinigung: 'Fiktionsbescheinigung', sonstige: 'Sonstiges Dokument' }[verifyDokumentTyp] || verifyDokumentTyp
                }</strong>
              </span>
            </div>
          )}

          {/* Summary Badge */}
          <div className={`flex items-center gap-2 p-3 rounded-xl ${
            allOk ? 'bg-green-50 dark:bg-green-900/10 border border-green-200' : 'bg-amber-50 dark:bg-amber-900/10 border border-amber-200'
          }`}>
            {allOk
              ? <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
              : <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />}
            <p className="text-sm font-semibold">
              {allOk ? 'Alle Angaben stimmen überein!' : 'Einige Angaben weichen vom Ausweis ab'}
            </p>
          </div>

          {/* Felder */}
          {Object.entries(verifyResults).map(([key, val]) => (
            <FieldCompareRow
              key={key}
              feldName={key}
              label={{ vorname: 'Vorname', familienname: 'Nachname', geburtsdatum: 'Geburtsdatum', geburtsort: 'Geburtsort', staatsangehoerigkeit: 'Staatsangehörigkeit' }[key] || key}
              result={val}
              override={fieldOverrides[key]}
              onOverrideChange={(name, checked) => setFieldOverrides(p => ({ ...p, [name]: checked }))}
            />
          ))}

          {/* Aufenthaltstitel-Ergebnis (nur wenn Aufenthaltstitel/NE erkannt) */}
          {verifyAufenthaltstitel && (verifyAufenthaltstitel.gueltigBis || verifyAufenthaltstitel.istUnbefristet !== null) && (
            <div className={`p-3 rounded-xl border ${
              verifyAufenthaltstitel.istUnbefristet
                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700'
                : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-700'
            }`}>
              <div className="flex items-start gap-2">
                {verifyAufenthaltstitel.istUnbefristet
                  ? <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className="text-sm font-semibold">
                    {verifyAufenthaltstitel.istUnbefristet ? 'Unbefristeter Aufenthalt erkannt' : 'Befristeter Aufenthalt erkannt'}
                  </p>
                  {verifyAufenthaltstitel.gueltigBis && (
                    <p className="text-xs mt-0.5">
                      Gültig bis: <strong>{verifyAufenthaltstitel.gueltigBis}</strong>
                    </p>
                  )}
                  {verifyAufenthaltstitel.aufenthaltszweck && (
                    <p className="text-xs text-muted-foreground mt-0.5">{verifyAufenthaltstitel.aufenthaltszweck}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {verifyAufenthaltstitel.gueltigBis
                      ? 'Das Ablaufdatum wurde automatisch in dein Formular übernommen.'
                      : verifyAufenthaltstitel.istUnbefristet
                      ? 'Dein Aufenthalt wurde als unbefristet eingetragen.'
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Korrektur-Button für abweichende Felder */}
          {Object.entries(verifyResults).filter(([k, v]) => v.übereinstimmung === false && !fieldOverrides[k] && v.ausweisWert).length > 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-xl">
              <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-1">
                <Info className="h-3.5 w-3.5" /> Vorschlag: Abweichende Felder übernehmen
              </p>
              {Object.entries(verifyResults)
                .filter(([k, v]) => v.übereinstimmung === false && !fieldOverrides[k] && v.ausweisWert)
                .map(([key, val]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleApplySuggestion(key, val.ausweisWert)}
                    className="block w-full text-left text-xs text-blue-700 dark:text-blue-400 hover:underline py-0.5"
                  >
                    → {['vorname','familienname','geburtsdatum','geburtsort','staatsangehoerigkeit'][['vorname','familienname','geburtsdatum','geburtsort','staatsangehoerigkeit'].indexOf(key)] || key} auf "<strong>{val.ausweisWert}</strong>" setzen
                  </button>
                ))
              }
            </div>
          )}
        </div>
      )}

      {/* IBAN-Verifikation (aufklappbar) */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setShowIban(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            IBAN-Prüfung (optional)
            {ibanVerifyResult?.übereinstimmung === true && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            {ibanVerifyResult?.übereinstimmung === false && <AlertTriangle className="h-4 w-4 text-amber-600" />}
          </span>
          {showIban ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>
        {showIban && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-muted-foreground pt-3">
              Fotografiere deine Bankkarte oder einen Kontoauszug mit der IBAN. Das Bild wird nicht gespeichert.
            </p>
            <PhotoUpload
              label="Bankkarte / Kontoauszug"
              value={ibanCardImage}
              onChange={setIbanCardImage}
              testId="iban-card-upload"
            />
            <Button
              type="button"
              onClick={handleVerifyIban}
              disabled={!ibanCardImage}
              variant="outline"
              className="w-full"
              data-testid="verify-iban-btn"
            >
              IBAN prüfen
            </Button>
            {ibanVerifyResult && (
              <div className={`p-3 rounded-lg border text-sm ${
                ibanVerifyResult.error ? 'bg-red-50 border-red-200 text-red-700' :
                ibanVerifyResult.übereinstimmung ? 'bg-green-50 border-green-200 text-green-700' :
                'bg-amber-50 border-amber-200 text-amber-700'
              }`}>
                {ibanVerifyResult.error
                  ? ibanVerifyResult.error
                  : ibanVerifyResult.übereinstimmung
                  ? `✓ IBAN stimmt überein (${ibanVerifyResult.konfidenz} Sicherheit)`
                  : `⚠ Abweichung: Gefunden: ${ibanVerifyResult.gefundeneIban || '—'}`
                }
              </div>
            )}
          </div>
        )}
      </div>

      {/* Überspringen */}
      <button
        type="button"
        onClick={() => setVerifyStep('skipped')}
        className="w-full text-xs text-muted-foreground hover:text-gray-600 py-2 transition-colors"
        data-testid="skip-verification-btn"
      >
        Prüfung überspringen →
      </button>
    </div>
  );
}
